import { useState } from "react";
import { ChevronDown } from "lucide-react";

interface ExchangeEntry {
  exchange_number: number;
  agent_message: string;
  agent_tactic: string;
  tactic_label: string;
  tactic_mechanism: string;
  user_response: string;
  user_response_assessment: string;
  ideal_response: string;
  neutralizer_logic: string;
}

export interface ExchangeAnalysisData {
  exchange_analysis: ExchangeEntry[];
  session_summary: string;
  strongest_exchange: number;
  growth_exchange: number;
}

interface ExchangeAnalysisLoadingProps { type: "loading"; }
interface ExchangeAnalysisErrorProps { type: "error"; message: string; }
interface ExchangeAnalysisLoadedProps { type: "loaded"; data: ExchangeAnalysisData; }

type ExchangeAnalysisSectionProps =
  | ExchangeAnalysisLoadingProps
  | ExchangeAnalysisErrorProps
  | ExchangeAnalysisLoadedProps;

// Compress multi-sentence forensic prose into a single observational line.
const oneLine = (s: string | undefined, max = 90): string => {
  if (!s) return "";
  const first = s.split(/(?<=[.!?])\s+/)[0]?.trim() || s.trim();
  if (first.length <= max) return first.replace(/\.$/, "") + ".";
  return first.slice(0, max - 1).trim() + "…";
};

const ExchangeRow = ({ ex }: { ex: ExchangeEntry }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-border/40 last:border-b-0">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between py-3 group"
      >
        <div className="flex items-center gap-3 min-w-0">
          <span className="text-[10px] font-mono tracking-[0.24em] uppercase text-muted-foreground/60">
            Exchange {ex.exchange_number}
          </span>
          <span className="text-xs text-foreground/70 font-light truncate">
            {ex.tactic_label}
          </span>
        </div>
        <ChevronDown
          className={`w-4 h-4 text-muted-foreground/50 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      <div
        className={`grid transition-all duration-300 ease-out ${
          open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
        }`}
      >
        <div className="overflow-hidden">
          <div className="pb-5 pt-1 space-y-4">
            <div className="space-y-1">
              <p className="text-[9px] font-mono tracking-[0.24em] uppercase text-muted-foreground/55">
                Simulated Message
              </p>
              <p className="text-sm text-foreground/85 italic font-light leading-relaxed">
                "{ex.agent_message}"
              </p>
            </div>

            <div className="space-y-1">
              <p className="text-[9px] font-mono tracking-[0.24em] uppercase text-amber-400/70">
                Pattern Observed
              </p>
              <p className="text-sm text-foreground/80 font-light">
                {ex.tactic_label}
              </p>
            </div>

            <div className="space-y-1">
              <p className="text-[9px] font-mono tracking-[0.24em] uppercase text-muted-foreground/55">
                Your Response
              </p>
              <p className="text-sm text-foreground/85 font-light leading-relaxed">
                {ex.user_response}
              </p>
            </div>

            <div className="space-y-1 border-l border-cyan/40 pl-3">
              <p className="text-[9px] font-mono tracking-[0.24em] uppercase text-cyan/70">
                Tactical Neutral Response
              </p>
              <p className="text-sm text-foreground/90 font-light leading-relaxed">
                {ex.ideal_response}
              </p>
            </div>

            <div className="space-y-1">
              <p className="text-[9px] font-mono tracking-[0.24em] uppercase text-muted-foreground/55">
                Why It Worked
              </p>
              <p className="text-xs text-foreground/65 font-light">
                {oneLine(ex.neutralizer_logic)}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ExchangeAnalysisSection = (props: ExchangeAnalysisSectionProps) => {
  const [groupOpen, setGroupOpen] = useState(false);

  return (
    <section className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="w-1 h-1 rounded-full bg-amber-400/60" />
        <p className="text-[10px] font-mono tracking-[0.28em] uppercase text-muted-foreground/70">
          Full Forensic Breakdown
        </p>
      </div>

      {props.type === "loading" && (
        <div className="flex items-center gap-3 py-6 justify-center">
          <span className="w-1.5 h-1.5 rounded-full bg-cyan/70 animate-pulse" />
          <p className="text-xs text-muted-foreground/70 font-light">
            Forensic analysis in progress…
          </p>
        </div>
      )}

      {props.type === "error" && (
        <p className="text-xs text-muted-foreground/70 font-light py-2">{props.message}</p>
      )}

      {props.type === "loaded" && (
        <div className="rounded-xl border border-border/40 bg-card/30 px-4">
          <button
            onClick={() => setGroupOpen((v) => !v)}
            className="w-full flex items-center justify-between py-3"
          >
            <span className="text-xs text-foreground/75 font-light">
              {props.data.exchange_analysis.length} exchanges analyzed
            </span>
            <ChevronDown
              className={`w-4 h-4 text-muted-foreground/50 transition-transform ${groupOpen ? "rotate-180" : ""}`}
            />
          </button>

          <div
            className={`grid transition-all duration-300 ease-out ${
              groupOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
            }`}
          >
            <div className="overflow-hidden">
              <div className="border-t border-border/40">
                {props.data.exchange_analysis.map((ex) => (
                  <ExchangeRow key={ex.exchange_number} ex={ex} />
                ))}
              </div>
              {props.data.session_summary && (
                <div className="py-4 border-t border-border/40">
                  <p className="text-[9px] font-mono tracking-[0.24em] uppercase text-muted-foreground/55 mb-1">
                    Session Assessment
                  </p>
                  <p className="text-sm text-foreground/80 font-light leading-relaxed">
                    {oneLine(props.data.session_summary, 180)}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default ExchangeAnalysisSection;
